
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { AccentId } from '../types';
import { createPcmBlob, decodeAudioData, base64ToUint8Array } from './audioUtils';

// Declare process.env for TypeScript since Vite replaces it at build time
declare const process: {
  env: {
    API_KEY: string;
  }
};

export class GeminiLiveService {
  private client: GoogleGenAI | null = null;
  private sessionPromise: Promise<any> | null = null;
  private audioContext: AudioContext | null = null;
  private nextStartTime: number = 0;
  private onSubtitle: ((text: string) => void) | null = null;
  private onComplete: ((text: string) => void) | null = null;
  private onPlayAudio: ((buffer: AudioBuffer) => void) | null = null;
  private onError: ((error: any) => void) | null = null;
  private currentTranscription = '';

  constructor() {
    // AudioContext must be initialized after user interaction in some browsers.
  }

  async connect(
    voiceName: string, // The API voice name (e.g., 'Puck', 'Fenrir')
    personaPrompt: string, // The specific personality instruction
    accent: AccentId,
    onSubtitle: (text: string) => void,
    onComplete: (text: string) => void,
    onPlayAudio: (buffer: AudioBuffer) => void,
    onError: (error: any) => void
  ) {
    // Robust API Key Retrieval
    let apiKey = process.env.API_KEY;
    
    if (!apiKey || apiKey === "") {
        // @ts-ignore
        if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_KEY) {
            // @ts-ignore
            apiKey = import.meta.env.VITE_API_KEY;
        }
    }

    if (!apiKey) {
        const errorMsg = "API Key not found. If using Vercel, add 'API_KEY' to Environment Variables and REDEPLOY.";
        console.error(errorMsg);
        onError(new Error(errorMsg));
        return;
    }

    this.client = new GoogleGenAI({ apiKey: apiKey });
    this.onSubtitle = onSubtitle;
    this.onComplete = onComplete;
    this.onPlayAudio = onPlayAudio;
    this.onError = onError;
    this.currentTranscription = '';

    // Initialize AudioContext for playback
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new AudioContextClass({
      sampleRate: 24000, // Gemini default output
    });
    
    // Resume audio context immediately (required for some mobile browsers)
    if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
    }
    
    this.nextStartTime = this.audioContext.currentTime;

    // PHONETIC ACCENT ENGINEERING
    // To make accents real, we tell the model to modify its spelling/grammar
    // so the TTS engine (which reads literally) sounds like the accent.
    let accentInstruction = "Speak in standard English.";
    
    if (accent && accent !== AccentId.Neutral) {
        if (accent === AccentId.Pirate) {
            accentInstruction = "ACCENT: PIRATE. Use words like 'Arrr', 'Matey', 'Ahoy'. Drop your 'g's (e.g. 'runnin'). Speak in a growly tone.";
        } else if (accent === AccentId.Southern) {
            accentInstruction = "ACCENT: SOUTHERN US. Drawl your vowels (e.g., 'weelll' for 'well'). Use 'y'all', 'fixin to', 'reckon'. Drop the 'g' in -ing words.";
        } else if (accent === AccentId.Posh) {
             accentInstruction = "ACCENT: POSH BRITISH (RP). Enunciate crisply. Drop the 'r' at the end of words (e.g., 'betta' for 'better'). Use vocabulary like 'splendid', 'ghastly', 'chap'.";
        } else if (accent === AccentId.British) {
             accentInstruction = "ACCENT: COCKNEY/WORKING CLASS BRITISH. Drop your 'h's (e.g., 'ello' for 'hello'). Replace 'th' with 'f' (e.g., 'fink' for 'think'). Use 'innit', 'mate'.";
        } else if (accent === AccentId.Australian) {
             accentInstruction = "ACCENT: AUSTRALIAN. Rise at the end of sentences? Use 'mate', 'crikey', 'no worries'. Flatten your vowels.";
        } else if (accent === AccentId.Mob) {
             accentInstruction = "ACCENT: NEW YORK ITALIAN MOBSTER. Replace 'th' with 'd' or 't' (e.g., 'dis', 'dat', 'dose'). Use 'fuggedaboutit', 'capiche', 'ey!'. Speak tough and nasal.";
        } else if (accent === AccentId.Irish) {
             accentInstruction = "ACCENT: IRISH. Speak with a lilt. Use words like 'grand', 'wee', 'lass/lad'. Pronounce 'th' as 't' (e.g., 'tink' for 'think', 'dat' for 'that'). End sentences with 'so I am' or 'to be sure'.";
        }
    }

    const config = {
      model: 'gemini-2.5-flash-native-audio-preview-09-2025',
      callbacks: {
        onopen: () => console.log('Gemini Live session opened'),
        onmessage: this.handleMessage.bind(this),
        onclose: () => console.log('Gemini Live session closed'),
        onerror: (e: any) => {
          console.error('Gemini Live session error', e);
          if (this.onError) this.onError(e);
        },
      },
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: voiceName } },
        },
        systemInstruction: `You are the voice of the dog visible in the camera.
            
            YOUR PERSONA:
            ${personaPrompt}

            ACCENT INSTRUCTION (CRITICAL):
            ${accentInstruction}

            MODES:
            1. TRANSLATION: When the dog barks, growls, or moves expressively, translate that into English based on your Persona.
            2. CONVERSATION: When a human speaks to the dog, respond *as the dog*. Answer questions, react to praise, or obey commands verbally.

            GUIDELINES:
            - Always speak in the FIRST PERSON ("I", "Me", "My").
            - Do NOT transcribe what the human says. Only speak the dog's response.
            - Match the energy of your Persona.
            - Keep responses short, punchy, and suitable for subtitles.
            - If nothing is happening and no one is talking, remain silent.`,
        outputAudioTranscription: {}, 
        inputAudioTranscription: {},
      },
    };

    try {
      this.sessionPromise = this.client.live.connect(config);
      await this.sessionPromise;
    } catch (e) {
      console.error("Failed to connect", e);
      throw e;
    }
  }

  disconnect() {
    if (this.sessionPromise) {
      this.sessionPromise.then((session) => {
        try {
          session.close();
        } catch (e) {
          console.warn("Error closing session", e);
        }
      });
      this.sessionPromise = null;
    }

    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch (e) {
        console.warn("Error closing audio context", e);
      }
      this.audioContext = null;
    }
  }

  private async handleMessage(message: LiveServerMessage) {
    if (!this.audioContext) return;

    // Handle Text Transcription
    const outputText = message.serverContent?.outputTranscription?.text;
    if (outputText) {
      this.currentTranscription += outputText;
      if (this.onSubtitle) {
          this.onSubtitle(this.currentTranscription);
      }
    }
    
    // Clear transcription on turn complete to keep subtitles fresh
    if (message.serverContent?.turnComplete) {
       if (this.onComplete && this.currentTranscription.trim()) {
           this.onComplete(this.currentTranscription.trim());
       }

       this.currentTranscription = '';
       setTimeout(() => {
           if(this.onSubtitle) this.onSubtitle('');
       }, 2000); 
    }

    // Handle Audio Output
    const audioData = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
    if (audioData) {
      try {
        const rawBytes = base64ToUint8Array(audioData);
        const audioBuffer = await decodeAudioData(rawBytes, this.audioContext, 24000);
        
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);
        
        const now = this.audioContext.currentTime;
        if (this.nextStartTime < now) {
          this.nextStartTime = now + 0.05; 
        }
        
        source.start(this.nextStartTime);
        this.nextStartTime += audioBuffer.duration;

        if (this.onPlayAudio) {
            this.onPlayAudio(audioBuffer);
        }

      } catch (e) {
        console.error("Error decoding/playing audio", e);
      }
    }
  }

  sendVideoFrame(base64Data: string) {
    if (!this.sessionPromise) return;
    
    this.sessionPromise.then((session) => {
      session.sendRealtimeInput({
        media: {
          mimeType: 'image/jpeg',
          data: base64Data
        }
      });
    });
  }

  sendAudioChunk(float32Data: Float32Array) {
    if (!this.sessionPromise) return;

    const pcmBlob = createPcmBlob(float32Data, 16000);
    this.sessionPromise.then((session) => {
      session.sendRealtimeInput({
        media: pcmBlob
      });
    });
  }
}